import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { Menu, Moon, Sun, X } from "lucide-react";
import { isLoggedIn } from "../lib/authSync";
import { setStorageMode } from "../lib/storageMode";

const sectionZoom = { home: 1.02, about: 1.07, "how-it-works": 1.04, subscription: 1.09 };

export default function Landing() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const scrollContainerRef = useRef(null);
  const isDark = theme === "dark";
  const appPath = isLoggedIn() ? "/app" : "/login";

  useEffect(() => {
    window.gtag?.("event", "page_view", {
      page_path: "/",
      page_title: "Landing",
    });
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll("[data-landing-section]");
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveSection(visible.target.id);
      },
      // Fires when a section crosses the vertical midpoint of the viewport,
      // so it works reliably no matter how tall a given section is.
      { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  function continueLocally() {
    const confirmed = window.confirm(
      "Continue locally? Your work will stay on this device and will not sync across devices. You do not need an account. Work above 20 MB cannot be automatically uploaded later unless you upgrade."
    );
    if (!confirmed) return;
    setStorageMode("local");
    navigate("/app");
  }
  const menuItems = [
    { label: "Home", href: "#home" },
    { label: "About us", href: "#about" },
    { label: "How it works", href: "#how-it-works" },
    { label: "Subscription", href: "#subscription" },
    { label: isLoggedIn() ? "Open app" : "Sign in", to: appPath },
  ];

  return (
    <main ref={scrollContainerRef} className="landing-scroll relative h-screen overflow-y-auto bg-slate-950 text-white">
      <img
        src={isDark ? "/landing_page_night.webp" : "/landing_page_day.webp"}
        alt="AnoteQuest canvas workspace"
        className="landing-background fixed inset-0 h-full w-full object-cover"
        style={{ transform: `scale(${sectionZoom[activeSection] || 1.02})` }}
      />
      <div className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-x-0 top-0 z-20 flex items-center justify-end gap-3 px-4 py-4 sm:px-8">
        <button
          type="button"
          aria-label={isDark ? "Switch to day mode" : "Switch to night mode"}
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white shadow-lg backdrop-blur-md transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        <div className="relative">
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white shadow-lg backdrop-blur-md transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/70"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <nav
            className={
              "absolute right-0 top-14 w-48 origin-top-right overflow-hidden rounded-lg border border-white/25 bg-white/15 text-sm text-white shadow-2xl backdrop-blur-xl transition-all duration-500 ease-out " +
              (menuOpen
                ? "max-h-96 translate-y-0 scale-100 p-2 opacity-100"
                : "pointer-events-none max-h-0 -translate-y-3 scale-95 p-0 opacity-0")
            }
          >
            {menuItems.map((item, index) => (
              item.to ? (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={
                    "block rounded-md px-4 py-3 transition-all duration-300 ease-out hover:bg-white/20 " +
                    (menuOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0")
                  }
                  style={{ transitionDelay: menuOpen ? `${index * 70}ms` : "0ms" }}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className={
                    "block rounded-md px-4 py-3 transition-all duration-300 ease-out hover:bg-white/20 " +
                    (menuOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0")
                  }
                  style={{ transitionDelay: menuOpen ? `${index * 70}ms` : "0ms" }}
                >
                  {item.label}
                </a>
              )
            ))}
          </nav>
        </div>
      </div>

      <section id="home" data-landing-section className="relative z-10 flex min-h-screen items-center justify-center px-4 py-24 text-center">
        <div className="flex max-w-3xl flex-col items-center">
          <div className="flex items-center gap-3 sm:gap-4">
            <img src="/logo1.png" alt="AnoteQuest logo" className="h-14 w-14 sm:h-20 sm:w-20" />
            <h1 className="text-5xl font-semibold tracking-normal text-white sm:text-7xl">
              A<span className="text-amber-300">note</span>Quest
            </h1>
          </div>
          <p className="mt-4 text-xl font-medium text-white/90 sm:text-2xl">
            From creators, for creators.
          </p>
          <div className="mt-8 flex w-full max-w-sm flex-col gap-3 sm:max-w-none sm:flex-row sm:justify-center">
            <Link
              to="/login?mode=signup"
              className="inline-flex min-h-12 items-center justify-center rounded-md bg-white px-6 py-3 text-sm font-semibold text-slate-950 shadow-xl transition hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/80"
            >
              Sign up free
            </Link>
            <Link
              to={appPath}
              className="inline-flex min-h-12 items-center justify-center rounded-md border border-white/35 bg-white/15 px-6 py-3 text-sm font-semibold text-white shadow-xl backdrop-blur-md transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/80"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      <section id="about" data-landing-section className="relative z-10 flex min-h-screen items-center justify-center px-4 py-24 text-center">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-normal text-white/70">About us</p>
          <h2 className="mt-4 text-4xl font-semibold tracking-normal text-white sm:text-6xl">
            A canvas ready to support your thoughts and ideas.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-white/85 sm:text-lg">
            Built by an artist and creator to serve other creatives, AnoteQuest gives your notes,
            images, sketches, sources, and tasks a shared space to grow. Start with a generous free
            plan, keep everything to yourself with local only mode, or choose a paid plan for extra
            storage and cross-device sync. Try AnoteQuest now, and do not let your inspiration run loose.
          </p>
        </div>
      </section>

      <section id="how-it-works" data-landing-section className="relative z-10 px-4 py-16 text-center">
        <div className="mx-auto max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-normal text-white/70">How it works</p>
          <h2 className="mt-4 text-4xl font-semibold tracking-normal text-white sm:text-6xl">
            Drop everything onto one living board.
          </h2>
          <div className="mt-6 space-y-4 text-left">
            {[
              ["Capture", "Add notes as soon as an idea arrives.", "https://pub-444cc6ffa6ed43fcbbac4d1942f68eb2.r2.dev/Addnotes.mp4"],
              ["Arrange", "Move ideas around until patterns, plans, and stories start to appear.", "https://pub-444cc6ffa6ed43fcbbac4d1942f68eb2.r2.dev/Arrange.mp4"],
              ["Add visuals", "Drag and drop images directly into your canvas.", "https://pub-444cc6ffa6ed43fcbbac4d1942f68eb2.r2.dev/Uploadimage.mp4"],
            ].map(([title, copy, videoSrc], index) => (
              <article key={title} className="flex items-stretch overflow-hidden rounded-xl border border-white/20 bg-white/10 shadow-xl backdrop-blur-md">
                <div className={`w-3/5 flex-shrink-0 overflow-hidden bg-black/20 sm:w-2/3 ${index === 1 ? "order-2" : ""}`}>
                  <video
                    className="h-full w-full object-cover"
                    src={videoSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                  />
                </div>
                <div className={`flex w-2/5 flex-col justify-center p-3 sm:w-1/3 sm:p-4 ${index === 1 ? "order-1" : ""}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-300 sm:text-xs">Step {index + 1}</p>
                  <h3 className="mt-1 text-base font-semibold text-white sm:text-xl">{title}</h3>
                  <p className="mt-1.5 text-xs leading-5 text-white/80 sm:text-sm sm:leading-6">{copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="subscription" data-landing-section className="relative z-10 flex min-h-screen items-center justify-center px-4 py-24 text-center">
        <div className="max-w-6xl">
          <p className="text-sm font-semibold uppercase tracking-normal text-white/70">Subscription</p>
          <h2 className="mt-4 text-4xl font-semibold tracking-normal text-white sm:text-6xl">
            Start free. Grow when your work does.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-white/85 sm:text-lg">
            Choose cloud sync, keep everything on one device, or get more room for your growing workspace.
          </p>
          <div className="mt-8 grid items-start gap-5 text-left md:grid-cols-3">
            {[
              {
                name: "Free sync", eyebrow: "Free", price: "$0", blurb: "For getting started with cloud-synced notes.",
                features: ["20 MB synced storage", "Sync across your devices", "Notes, boards, images, and tools", "No credit card required"],
                action: "Sign up free", to: "/login?mode=signup",
              },
              {
                name: "Local", eyebrow: "On this device", price: "$0", blurb: "For working fully offline, no account needed.",
                features: ["No AnoteQuest 20 MB cap", "Saved privately on this device", "No account or cross-device sync", "Export or back up anytime"],
                action: "Continue locally", local: true,
              },
              {
                name: "Paid", eyebrow: "For growing workspaces", price: "$5", priceSuffix: "/month", blurb: "For creators who outgrow the free tier.",
                features: ["Unlimited synced storage — no 20 MB cap", "No 100-note limit on your canvas", "Sync across every device automatically", "Priority support, cancel anytime"],
                action: "Get Premium", to: "/subscribe", featured: true,
              },
            ].map((plan) => (
              <article key={plan.name} className={`mx-auto flex min-h-[460px] w-full max-w-xs flex-col rounded-xl border p-6 shadow-2xl backdrop-blur-xl ${plan.featured ? "border-amber-300/80 bg-amber-200/20" : "border-white/25 bg-white/10"}`}>
                <p className="text-xs font-semibold uppercase tracking-widest text-white/65">{plan.eyebrow}</p>
                <h3 className="mt-3 text-2xl font-semibold text-white">{plan.name}</h3>
                <div className="mt-1.5 flex items-end gap-1">
                  <span className="text-xl font-semibold text-white/90">{plan.price}</span>
                  {plan.priceSuffix && <span className="pb-0.5 text-sm text-white/60">{plan.priceSuffix}</span>}
                </div>
                <p className="mt-1.5 text-sm text-white/70">{plan.blurb}</p>
                <ul className="mt-5 flex-1 space-y-3 text-sm leading-6 text-white/85">
                  {plan.features.map((feature) => <li key={feature} className="flex gap-2"><span className="text-amber-300">✓</span>{feature}</li>)}
                </ul>
                {plan.local ? (
                  <button
                    type="button"
                    onClick={continueLocally}
                    className="mt-7 inline-flex min-h-11 items-center justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/80"
                  >
                    {plan.action}
                  </button>
                ) : (
                  <Link
                    to={plan.to}
                    className={`mt-7 inline-flex min-h-11 items-center justify-center rounded-md px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-white/80 ${plan.featured ? "bg-amber-300 text-slate-950 hover:bg-amber-200" : "bg-white text-slate-950 hover:bg-white/90"}`}
                  >
                    {plan.action}
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <footer id="footer" data-landing-section className="relative z-10 border-t border-white/10 bg-slate-950 px-4 py-16 text-left sm:px-8">
        <div className="mx-auto grid max-w-6xl gap-10 sm:grid-cols-2 md:grid-cols-4">
          <div>
            <p className="text-2xl font-semibold text-white">
              A<span className="text-amber-300">note</span>Quest
            </p>
            <p className="mt-3 max-w-xs text-sm leading-6 text-white/60">
              A canvas ready to support your thoughts and ideas. From creators, for creators.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50">Product</p>
            <ul className="mt-4 space-y-3 text-sm text-white/75">
              <li><a href="#home" className="transition hover:text-white">Home</a></li>
              <li><a href="#about" className="transition hover:text-white">About us</a></li>
              <li><a href="#how-it-works" className="transition hover:text-white">How it works</a></li>
              <li><a href="#subscription" className="transition hover:text-white">Subscription</a></li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50">Legal</p>
            <ul className="mt-4 space-y-3 text-sm text-white/75">
              <li><Link to="/terms" className="transition hover:text-white">Terms and Conditions</Link></li>
              <li><Link to="/privacy" className="transition hover:text-white">Privacy Policy</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50">Support</p>
            <ul className="mt-4 space-y-3 text-sm text-white/75">
              <li><Link to="/contact" className="transition hover:text-white">Contact us</Link></li>
              <li>
                <a href="mailto:support@anotequest.com" className="transition hover:text-white">
                  support@anotequest.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-6xl border-t border-white/10 pt-6 text-xs text-white/45">
          <p>&copy; {new Date().getFullYear()} AnoteQuest. All rights reserved.</p>
        </div>
      </footer>
    </main>
  );
}
